import React, { useEffect, useState } from "react";
import "../../components/projects/Projects.css";
import { tenantAPI } from "../../api/client";
import useAuth from "../../hooks/useAuth";
import { STATUS_LIST, DEFAULT_STATUS_COLORS } from "../../utils/statusColors";
import {
  SYSTEM_CARD_FIELDS,
  buildOrderedFieldKeys,
} from "../../utils/cardFieldOrder";

const BUILT_IN_FIELDS = [
  { key: "name", label: "Project Name", alwaysMandatory: true },
  { key: "customer", label: "Customer", alwaysMandatory: true },
  { key: "workflow", label: "Workflow", alwaysMandatory: true },
  { key: "duration", label: "Duration", alwaysMandatory: true },
  { key: "description", label: "Description", alwaysMandatory: false },
  { key: "images", label: "Project Images", alwaysMandatory: false },
  { key: "stlFiles", label: "3D Model Files (.stl)", alwaysMandatory: false },
  { key: "documents", label: "Project Documents", alwaysMandatory: false },
  { key: "folder", label: "Assign to Folder", alwaysMandatory: false },
  { key: "initialAllocation", label: "Initial Allocation", alwaysMandatory: false },
];

const DEFAULT_SYSTEM_FIELD_CONFIG = {
  description: { visible: true, required: false },
  images: { visible: true, required: false },
  stlFiles: { visible: true, required: false },
  documents: { visible: true, required: false },
  folder: { visible: true, required: false },
  initialAllocation: { visible: true, required: false },
};

export default function CustomizationPage() {
  const {
    user,
    statusColorConfig,
    setStatusColorConfig,
    invoicingEnabled,
    setInvoicingEnabled,
  } = useAuth();

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [warningThreshold, setWarningThreshold] = useState(80);
  const [overdueThreshold, setOverdueThreshold] = useState(100);
  const [projectFormFields, setProjectFormFields] = useState([]);
  const [formSaving, setFormSaving] = useState(false);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [shiftSaving, setShiftSaving] = useState(false);
  const [invoicingInputType, setInvoicingInputType] = useState("text");
  const [invoicingSaving, setInvoicingSaving] = useState(false);
  const [invoicingSaved, setInvoicingSaved] = useState(false);
  const [statusColors, setStatusColors] = useState({
    ...DEFAULT_STATUS_COLORS,
  });
  const [logoSaved, setLogoSaved] = useState(false);
  const [thresholdsSaved, setThresholdsSaved] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [shiftSaved, setShiftSaved] = useState(false);
  const [colorsSaving, setColorsSaving] = useState(false);
  const [colorsSaved, setColorsSaved] = useState(false);
  const [cardFieldOrder, setCardFieldOrder] = useState([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [orderSaved, setOrderSaved] = useState(false);
  const [systemFieldConfig, setSystemFieldConfig] = useState({ ...DEFAULT_SYSTEM_FIELD_CONFIG });

  useEffect(() => {
    tenantAPI.getTenant().then((resp) => {
      const tenant = resp?.data || {};
      setWarningThreshold(parseInt(tenant.minimumWarning ?? 80, 10));
      setOverdueThreshold(parseInt(tenant.minimumOverdue ?? 100, 10));
      const schema = tenant.projectFormSchema || {};
      const fields = Array.isArray(schema.fields) ? schema.fields : [];
      const shifts = Array.isArray(schema.shifts) ? schema.shifts : [];
      setProjectFormFields(
        fields.map((field) => ({
          id: field.id || `${Date.now()}-${Math.random()}`,
          label: field.label || "",
          key: field.key || "",
          type: field.type || "text",
          required: !!field.required,
          showOnProjectCard: !!field.showOnProjectCard,
          highlightOnCard: !!field.highlightOnCard,
          options: Array.isArray(field.options) ? field.options : [],
        })),
      );
      setShiftTemplates(
        shifts.map((shift) => ({
          id: shift.id || `${Date.now()}-${Math.random()}`,
          name: String(shift.name || "").trim(),
          workStartTime: shift.workStartTime || "09:00",
          workEndTime: shift.workEndTime || "18:00",
        })),
      );
      setInvoicingInputType(schema.invoicingInputType || "text");
      setInvoicingEnabled(schema.invoicingEnabled !== false);
      setStatusColorConfig(tenant.statusColorConfig || null);
      const loadedFields = Array.isArray(schema.fields) ? schema.fields : [];
      const customCardKeys = loadedFields
        .filter((f) => f?.key && f?.label && f.showOnProjectCard)
        .map((f) => f.key);
      setCardFieldOrder(
        buildOrderedFieldKeys(schema.cardFieldOrder, customCardKeys),
      );
      setSystemFieldConfig({
        ...DEFAULT_SYSTEM_FIELD_CONFIG,
        ...(schema.systemFieldConfig || {}),
      });
    });
  }, [setStatusColorConfig]);

  useEffect(() => {
    setStatusColors({ ...DEFAULT_STATUS_COLORS, ...(statusColorConfig || {}) });
  }, [statusColorConfig]);

  const onFileChange = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
  };

  const handleSaveLogo = async () => {
    setSaving(true);
    setLogoSaved(false);
    try {
      await tenantAPI.customizeTenantImage(file);
      setLogoSaved(true);
      setTimeout(() => setLogoSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveThresholds = async () => {
    setSaving(true);
    setThresholdsSaved(false);
    try {
      await tenantAPI.customizeTenantData(
        Number(warningThreshold),
        Number(overdueThreshold),
      );
      setThresholdsSaved(true);
      setTimeout(() => setThresholdsSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const addField = () => {
    setProjectFormFields((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        label: "",
        key: "",
        type: "text",
        required: false,
        showOnProjectCard: false,
        highlightOnCard: false,
        options: [],
      },
    ]);
  };

  const updateField = (id, updates) => {
    setProjectFormFields((prev) =>
      prev.map((field) => (field.id === id ? { ...field, ...updates } : field)),
    );
  };

  const removeField = (id) => {
    setProjectFormFields((prev) => prev.filter((field) => field.id !== id));
  };

  const addOption = (id) => {
    setProjectFormFields((prev) =>
      prev.map((field) =>
        field.id === id
          ? { ...field, options: [...(field.options || []), ""] }
          : field,
      ),
    );
  };

  const updateOption = (id, index, value) => {
    setProjectFormFields((prev) =>
      prev.map((field) => {
        if (field.id !== id) return field;
        const next = [...(field.options || [])];
        next[index] = value;
        return { ...field, options: next };
      }),
    );
  };

  const removeOption = (id, index) => {
    setProjectFormFields((prev) =>
      prev.map((field) => {
        if (field.id !== id) return field;
        const next = [...(field.options || [])];
        next.splice(index, 1);
        return { ...field, options: next };
      }),
    );
  };

  const handleSaveProjectForm = async () => {
    const cleaned = projectFormFields.map((field) => ({
      id: field.id,
      label: String(field.label || "").trim(),
      key: String(field.key || "").trim(),
      type: field.type || "text",
      required: !!field.required,
      showOnProjectCard: !!field.showOnProjectCard,
      highlightOnCard: !!field.highlightOnCard,
      options:
        field.type === "select"
          ? (field.options || []).map((opt) => String(opt || "").trim())
          : [],
    }));
    const cleanedShifts = shiftTemplates
      .map((shift) => ({
        id: shift.id,
        name: String(shift.name || "").trim(),
        workStartTime: shift.workStartTime || "09:00",
        workEndTime: shift.workEndTime || "18:00",
      }))
      .filter((shift) => shift.name);

    const hasEmptyLabel = cleaned.some((field) => !field.label);
    const hasEmptyKey = cleaned.some((field) => !field.key);
    const keySet = new Set(cleaned.map((field) => field.key));
    const hasDuplicateKey = keySet.size !== cleaned.length;
    const hasEmptyOptions = cleaned.some(
      (field) =>
        field.type === "select" &&
        (!field.options.length || field.options.some((opt) => !opt.trim())),
    );

    if (hasEmptyLabel || hasEmptyKey) {
      alert("All fields must have a label and a key.");
      return;
    }
    if (hasDuplicateKey) {
      alert("Each field key must be unique.");
      return;
    }
    if (hasEmptyOptions) {
      alert("Dropdown fields must have at least one option.");
      return;
    }

    setFormSaving(true);
    setFormSaved(false);
    try {
      await tenantAPI.customizeProjectForm({
        fields: cleaned,
        shifts: cleanedShifts,
        invoicingInputType,
        invoicingEnabled,
        cardFieldOrder,
        systemFieldConfig,
      });
      setFormSaved(true);
      setTimeout(() => setFormSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setFormSaving(false);
    }
  };

  const addShiftTemplate = () => {
    setShiftTemplates((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        name: "",
        workStartTime: "09:00",
        workEndTime: "18:00",
      },
    ]);
  };

  const updateShiftTemplate = (id, updates) => {
    setShiftTemplates((prev) =>
      prev.map((shift) => (shift.id === id ? { ...shift, ...updates } : shift)),
    );
  };

  const removeShiftTemplate = (id) => {
    setShiftTemplates((prev) => prev.filter((shift) => shift.id !== id));
  };

  const handleSaveShiftTemplates = async () => {
    const cleanedShifts = shiftTemplates.map((shift) => ({
      id: shift.id,
      name: String(shift.name || "").trim(),
      workStartTime: shift.workStartTime || "09:00",
      workEndTime: shift.workEndTime || "18:00",
    }));

    const hasEmptyName = cleanedShifts.some((shift) => !shift.name);
    const hasInvalidHours = cleanedShifts.some(
      (shift) => shift.workStartTime >= shift.workEndTime,
    );
    const nameSet = new Set(
      cleanedShifts.map((shift) => shift.name.toLowerCase()),
    );
    const hasDuplicateName = nameSet.size !== cleanedShifts.length;

    if (hasEmptyName) {
      alert("Every shift must have a name.");
      return;
    }
    if (hasInvalidHours) {
      alert("Shift start time must be earlier than end time.");
      return;
    }
    if (hasDuplicateName) {
      alert("Shift names must be unique.");
      return;
    }

    const cleanedFields = projectFormFields.map((field) => ({
      id: field.id,
      label: String(field.label || "").trim(),
      key: String(field.key || "").trim(),
      type: field.type || "text",
      required: !!field.required,
      showOnProjectCard: !!field.showOnProjectCard,
      highlightOnCard: !!field.highlightOnCard,
      options:
        field.type === "select"
          ? (field.options || []).map((opt) => String(opt || "").trim())
          : [],
    }));

    setShiftSaving(true);
    setShiftSaved(false);
    try {
      await tenantAPI.customizeProjectForm({
        fields: cleanedFields,
        shifts: cleanedShifts,
        invoicingInputType,
        invoicingEnabled,
        cardFieldOrder,
        systemFieldConfig,
      });
      setShiftSaved(true);
      setTimeout(() => setShiftSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setShiftSaving(false);
    }
  };

  const handleSaveInvoicingConfig = async () => {
    setInvoicingSaving(true);
    setInvoicingSaved(false);
    try {
      await tenantAPI.customizeProjectForm({
        fields: projectFormFields.map((f) => ({
          id: f.id,
          label: String(f.label || "").trim(),
          key: String(f.key || "").trim(),
          type: f.type || "text",
          required: !!f.required,
          showOnProjectCard: !!f.showOnProjectCard,
          highlightOnCard: !!f.highlightOnCard,
          options:
            f.type === "select"
              ? (f.options || []).map((o) => String(o || "").trim())
              : [],
        })),
        shifts: shiftTemplates.map((s) => ({
          id: s.id,
          name: String(s.name || "").trim(),
          workStartTime: s.workStartTime || "09:00",
          workEndTime: s.workEndTime || "18:00",
        })),
        invoicingInputType,
        invoicingEnabled,
        cardFieldOrder,
        systemFieldConfig,
      });
      setInvoicingSaved(true);
      setTimeout(() => setInvoicingSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setInvoicingSaving(false);
    }
  };

  const moveCardField = (index, direction) => {
    setCardFieldOrder((prev) => {
      const next = [...prev];
      const swapIndex = index + direction;
      if (swapIndex < 0 || swapIndex >= next.length) return prev;
      [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
      return next;
    });
  };

  const handleSaveCardFieldOrder = async () => {
    setOrderSaving(true);
    setOrderSaved(false);
    try {
      await tenantAPI.customizeProjectForm({
        fields: projectFormFields.map((f) => ({
          id: f.id,
          label: String(f.label || "").trim(),
          key: String(f.key || "").trim(),
          type: f.type || "text",
          required: !!f.required,
          showOnProjectCard: !!f.showOnProjectCard,
          highlightOnCard: !!f.highlightOnCard,
          options:
            f.type === "select"
              ? (f.options || []).map((o) => String(o || "").trim())
              : [],
        })),
        shifts: shiftTemplates.map((s) => ({
          id: s.id,
          name: String(s.name || "").trim(),
          workStartTime: s.workStartTime || "09:00",
          workEndTime: s.workEndTime || "18:00",
        })),
        invoicingInputType,
        invoicingEnabled,
        cardFieldOrder,
        systemFieldConfig,
      });
      setOrderSaved(true);
      setTimeout(() => setOrderSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setOrderSaving(false);
    }
  };

  const updateSystemFieldConfig = (key, updates) => {
    setSystemFieldConfig((prev) => {
      const current = prev[key] || { visible: true, required: false };
      const next = { ...current, ...updates };
      if (!next.visible) next.required = false;
      return { ...prev, [key]: next };
    });
  };

  const handleSaveStatusColors = async () => {
    setColorsSaving(true);
    setColorsSaved(false);
    try {
      await tenantAPI.saveStatusColors(statusColors);
      setStatusColorConfig(statusColors);
      setColorsSaved(true);
      setTimeout(() => setColorsSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setColorsSaving(false);
    }
  };

  return (
    <div className="customization-page">
      <div className="page-header">
        <h1 style={{ margin: 0 }}>Customization</h1>
      </div>

      <div className="panel" style={{ maxWidth: 600 }}>
        <h3 style={{ marginTop: 0 }}>Custom Logo</h3>
        <p className="small-muted">
          Upload a custom logo to personalize your workspace.
        </p>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onFileChange(e.target.files?.[0])}
        />
        {previewUrl && (
          <div style={{ marginTop: 12 }}>
            <img
              src={previewUrl}
              alt="Logo preview"
              style={{
                maxHeight: 80,
                maxWidth: "100%",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
              }}
            />
          </div>
        )}
        <div className="modal-actions">
          <button
            className="btn-primary"
            onClick={handleSaveLogo}
            disabled={saving || !file}
          >
            {saving ? "Saving..." : logoSaved ? "Saved ✓" : "Save"}
          </button>
        </div>
      </div>

      <div className="panel" style={{ maxWidth: 600, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Alert Thresholds</h3>
        <p className="small-muted">
          Adjust when alerts should trigger based on project time elapsed.
        </p>
        <div style={{ marginTop: 12 }}>
          <label className="small-muted">
            Warning Threshold: {warningThreshold}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={warningThreshold}
            onChange={(e) => setWarningThreshold(Number(e.target.value))}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <label className="small-muted">
            Overdue Threshold: {overdueThreshold}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={overdueThreshold}
            onChange={(e) => {
              setOverdueThreshold(Number(e.target.value));
            }}
          />
        </div>
        <div className="modal-actions">
          <button
            className="btn-primary"
            onClick={() => {
              if (Number(overdueThreshold) < warningThreshold) {
                alert(
                  "Overdue threshold must be greater than warning threshold",
                );
                return;
              }

              handleSaveThresholds();
            }}
            disabled={saving}
          >
            {saving ? "Saving..." : thresholdsSaved ? "Saved ✓" : "Save"}
          </button>
        </div>
      </div>

      {user?.isAdmin && (
        <div className="panel" style={{ maxWidth: 600, marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Invoicing</h3>
          <p className="small-muted">
            When enabled, completing the last workflow stage automatically
            queues the project for invoicing. The project stays in-progress
            until invoiced.
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginTop: 12,
            }}
          >
            <input
              type="checkbox"
              id="invoicingEnabled"
              checked={invoicingEnabled}
              onChange={(e) => setInvoicingEnabled(e.target.checked)}
            />
            <label
              htmlFor="invoicingEnabled"
              style={{ fontSize: 13, color: "#111827" }}
            >
              Enable invoicing
            </label>
          </div>
          {invoicingEnabled && (
            <div style={{ marginTop: 16 }}>
              <label
                className="small-muted"
                style={{ display: "block", marginBottom: 6 }}
              >
                Input required when marking as invoiced
              </label>
              <select
                value={invoicingInputType}
                onChange={(e) => setInvoicingInputType(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  fontSize: 13,
                }}
              >
                <option value="text">Invoice Number (text input)</option>
                <option value="checkbox">Confirmation checkbox</option>
                <option value="file">Invoice file upload</option>
              </select>
            </div>
          )}
          <div className="modal-actions">
            <button
              className="btn-primary"
              onClick={handleSaveInvoicingConfig}
              disabled={invoicingSaving}
            >
              {invoicingSaving
                ? "Saving..."
                : invoicingSaved
                  ? "Saved ✓"
                  : "Save"}
            </button>
          </div>
        </div>
      )}

      {user?.isAdmin && (
        <div className="panel" style={{ maxWidth: 600, marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Task Status Colors</h3>
          <p className="small-muted">
            Customise the color used for each task status across the planner,
            team view, and activity feed. Changes apply to all users
            immediately.
          </p>

          <div style={{ display: "grid", gap: 8, marginTop: 16 }}>
            {STATUS_LIST.map(({ key, label, defaultColor }) => {
              const currentColor = statusColors[key] || defaultColor;
              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: "#fafafa",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        backgroundColor: `${currentColor}26`,
                        borderLeft: `4px solid ${currentColor}`,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      {label}
                    </span>
                  </div>

                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {currentColor !== defaultColor && (
                      <button
                        type="button"
                        onClick={() =>
                          setStatusColors((prev) => ({
                            ...prev,
                            [key]: defaultColor,
                          }))
                        }
                        style={{
                          border: "none",
                          background: "transparent",
                          fontSize: 11,
                          color: "#9ca3af",
                          cursor: "pointer",
                          padding: "2px 6px",
                        }}
                      >
                        Reset
                      </button>
                    )}
                    <input
                      type="color"
                      value={currentColor}
                      onChange={(e) =>
                        setStatusColors((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      style={{
                        width: 36,
                        height: 36,
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        cursor: "pointer",
                        padding: 2,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="modal-actions">
            <button
              className="btn-primary"
              onClick={handleSaveStatusColors}
              disabled={colorsSaving}
            >
              {colorsSaving
                ? "Saving..."
                : colorsSaved
                  ? "Saved ✓"
                  : "Save Colors"}
            </button>
          </div>
        </div>
      )}

      {user?.isAdmin && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="page-header" style={{ marginBottom: 10 }}>
            <div>
              <h3 style={{ marginTop: 0 }}>Shift Templates</h3>
              <p className="small-muted">
                Define org-wide shifts for user schedule assignment.
              </p>
            </div>
            <button className="btn-secondary" onClick={addShiftTemplate}>
              + Add Shift
            </button>
          </div>

          {shiftTemplates.length === 0 && (
            <div className="small-muted">No shifts configured yet.</div>
          )}

          <div className="form-builder-list">
            {shiftTemplates.map((shift) => (
              <div key={shift.id} className="form-builder-card">
                <div className="form-builder-grid">
                  <div>
                    <label>Shift Name</label>
                    <input
                      value={shift.name}
                      onChange={(e) =>
                        updateShiftTemplate(shift.id, { name: e.target.value })
                      }
                      placeholder="e.g. Morning Shift"
                    />
                  </div>
                  <div>
                    <label>Start Time</label>
                    <input
                      type="time"
                      value={shift.workStartTime}
                      onChange={(e) =>
                        updateShiftTemplate(shift.id, {
                          workStartTime: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label>End Time</label>
                    <input
                      type="time"
                      value={shift.workEndTime}
                      onChange={(e) =>
                        updateShiftTemplate(shift.id, {
                          workEndTime: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="form-builder-actions">
                  <button
                    className="btn-danger"
                    type="button"
                    onClick={() => removeShiftTemplate(shift.id)}
                  >
                    Delete Shift
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button
              className="btn-primary"
              onClick={handleSaveShiftTemplates}
              disabled={shiftSaving}
            >
              {shiftSaving
                ? "Saving..."
                : shiftSaved
                  ? "Saved ✓"
                  : "Save Shifts"}
            </button>
          </div>
        </div>
      )}

      {user?.isAdmin && (
        <div className="panel" style={{ maxWidth: 600, marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Project Card Field Order</h3>
          <p className="small-muted">
            Set the order in which fields appear on each project card. Use the
            arrows to reposition fields.
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginTop: 12,
            }}
          >
            {cardFieldOrder.map((key, index) => {
              const systemField = SYSTEM_CARD_FIELDS.find((f) => f.key === key);
              const customField = projectFormFields.find((f) => f.key === key);
              const label = systemField?.label || customField?.label || key;
              const isSystem = !!systemField;
              return (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    background: isSystem ? "#f8fafc" : "#eff6ff",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: isSystem ? "#9ca3af" : "#3b82f6",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        minWidth: 48,
                      }}
                    >
                      {isSystem ? "System" : "Custom"}
                    </span>
                    <span style={{ fontSize: 13, color: "#111827" }}>
                      {label}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      onClick={() => moveCardField(index, -1)}
                      disabled={index === 0}
                      style={{
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        borderRadius: 4,
                        padding: "2px 8px",
                        cursor: index === 0 ? "not-allowed" : "pointer",
                        color: index === 0 ? "#d1d5db" : "#374151",
                        fontSize: 13,
                      }}
                    >
                      &#8593;
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCardField(index, 1)}
                      disabled={index === cardFieldOrder.length - 1}
                      style={{
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        borderRadius: 4,
                        padding: "2px 8px",
                        cursor:
                          index === cardFieldOrder.length - 1
                            ? "not-allowed"
                            : "pointer",
                        color:
                          index === cardFieldOrder.length - 1
                            ? "#d1d5db"
                            : "#374151",
                        fontSize: 13,
                      }}
                    >
                      &#8595;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="modal-actions">
            <button
              className="btn-primary"
              onClick={handleSaveCardFieldOrder}
              disabled={orderSaving}
            >
              {orderSaving
                ? "Saving..."
                : orderSaved
                  ? "Saved ✓"
                  : "Save Order"}
            </button>
          </div>
        </div>
      )}

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="page-header" style={{ marginBottom: 10 }}>
          <div>
            <h3 style={{ marginTop: 0 }}>Add Project Form Builder</h3>
            <p className="small-muted">
              Configure additional fields for the Add Project page. Use unique
              keys to store each value.
            </p>
          </div>
          <button className="btn-secondary" onClick={addField}>
            + Add Field
          </button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: "0 0 8px 0", fontSize: 13, color: "#374151" }}>Built-in Fields</h4>
          <div className="form-builder-list">
            {BUILT_IN_FIELDS.map((field) => {
              const cfg = field.alwaysMandatory
                ? { visible: true, required: true }
                : (systemFieldConfig[field.key] || { visible: true, required: false });
              return (
                <div key={field.key} className="form-builder-card" style={{ padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {field.alwaysMandatory && (
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>System</span>
                      )}
                      <span style={{ fontSize: 13, color: "#111827" }}>{field.label}</span>
                    </div>
                    <div style={{ display: "flex", gap: 20 }}>
                      <div className="form-builder-inline">
                        <label style={{ opacity: field.alwaysMandatory ? 0.5 : 1 }}>Visible</label>
                        <input
                          type="checkbox"
                          checked={cfg.visible}
                          disabled={field.alwaysMandatory}
                          onChange={(e) => updateSystemFieldConfig(field.key, { visible: e.target.checked })}
                        />
                      </div>
                      <div className="form-builder-inline">
                        <label style={{ opacity: (field.alwaysMandatory || !cfg.visible) ? 0.5 : 1 }}>Required</label>
                        <input
                          type="checkbox"
                          checked={cfg.required}
                          disabled={field.alwaysMandatory || !cfg.visible}
                          onChange={(e) => updateSystemFieldConfig(field.key, { required: e.target.checked })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <h4 style={{ margin: "0 0 8px 0", fontSize: 13, color: "#374151" }}>Custom Fields</h4>

        {projectFormFields.length === 0 && (
          <div className="small-muted">No custom fields configured.</div>
        )}

        <div className="form-builder-list">
          {projectFormFields.map((field) => (
            <div key={field.id} className="form-builder-card">
              <div className="form-builder-grid">
                <div>
                  <label>Label</label>
                  <input
                    value={field.label}
                    onChange={(e) =>
                      updateField(field.id, { label: e.target.value })
                    }
                    placeholder="e.g. Project Code"
                  />
                </div>
                <div>
                  <label>Key</label>
                  <input
                    value={field.key}
                    onChange={(e) =>
                      updateField(field.id, { key: e.target.value })
                    }
                    placeholder="e.g. projectCode"
                  />
                </div>
                <div>
                  <label>Type</label>
                  <select
                    value={field.type}
                    onChange={(e) =>
                      updateField(field.id, { type: e.target.value })
                    }
                  >
                    <option value="text">Text</option>
                    <option value="select">Dropdown</option>
                  </select>
                </div>
                <div className="form-builder-inline">
                  <label>Required</label>
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) =>
                      updateField(field.id, { required: e.target.checked })
                    }
                  />
                </div>
                <div className="form-builder-inline">
                  <label>Show on project card</label>
                  <input
                    type="checkbox"
                    checked={!!field.showOnProjectCard}
                    onChange={(e) =>
                      updateField(field.id, {
                        showOnProjectCard: e.target.checked,
                        highlightOnCard: e.target.checked
                          ? field.highlightOnCard
                          : false,
                      })
                    }
                  />
                </div>
                <div className="form-builder-inline">
                  <label style={{ opacity: field.showOnProjectCard ? 1 : 0.4 }}>
                    Highlight on card
                  </label>
                  <input
                    type="checkbox"
                    checked={!!field.highlightOnCard}
                    disabled={!field.showOnProjectCard}
                    onChange={(e) =>
                      updateField(field.id, {
                        highlightOnCard: e.target.checked,
                      })
                    }
                  />
                </div>
              </div>

              {field.type === "select" && (
                <div className="form-builder-options">
                  <div className="form-builder-options-header">
                    <span className="small-muted">Dropdown options</span>
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => addOption(field.id)}
                    >
                      + Add Option
                    </button>
                  </div>
                  {(field.options || []).map((option, idx) => (
                    <div key={`${field.id}-${idx}`} className="option-row">
                      <input
                        value={option}
                        onChange={(e) =>
                          updateOption(field.id, idx, e.target.value)
                        }
                        placeholder="Option value"
                      />
                      <button
                        className="btn-danger"
                        type="button"
                        onClick={() => removeOption(field.id, idx)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-builder-actions">
                <button
                  className="btn-danger"
                  type="button"
                  onClick={() => removeField(field.id)}
                >
                  Delete Field
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button
            className="btn-primary"
            onClick={handleSaveProjectForm}
            disabled={formSaving}
          >
            {formSaving ? "Saving..." : formSaved ? "Saved ✓" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
